import React, { useState } from "react";
import axios from "axios";
import Turnstile from "react-turnstile";
import claimsLogo from "./assets/claims-logo.png";
import speedrabbit from "./assets/speedrabbit-animate.gif";
import Verify from "./Verify";

const CLAIMS_API_URL = "https://api-suppliers.boholgov.com/hrisext/searchClaims";
const TRACKING_PATTERN = /^[0-9]{2}[A-Z]{2}[0-9]{2}-[0-9]{2}[A-Z]{2}[0-9]{4}$/;

const claimsApi = axios.create({
  timeout: 15000,
  xsrfCookieName: null,
  xsrfHeaderName: null,
  headers: {
    Accept: "application/json",
  },
});

function App() {
  const [trackingNo, setTrackingNo] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [transactionDetails, settransactionDetails] = useState(null);
  const [trasnactionTracking, settrasnactionTracking] = useState(null);
  const [transactionIsPending, settransactionIsPending] = useState(false);
  const [transactionPendingRemarks, settransactionPendingRemarks] = useState("");

  // console.log('$$TURNSTILE_SITE_KEY', process.env.TURNSTILE_SITE_KEY);
  const [verified, setVerified] = useState(false);

  if (!verified) {
    return <Verify onVerified={() => setVerified(true)} />;
  }

  const handleSearch = async (e) => {
    e.preventDefault();
    setError("");
    setData(null);
    settransactionDetails(null);
    settrasnactionTracking(null);
    settransactionIsPending(false);
    settransactionPendingRemarks("");

    // const token = sessionStorage.getItem("verifiedToken");

    // if (!token) {
    //   // This case should not happen if !verified renders <Verify />, but it's a good safety check
    //   setError("⚠️ Verification token missing. Please refresh.");
    //   return;
    // }

    if (!turnstileToken) {
      setError("⚠️ Please complete the verification first.");
      return;
    }

    if (!trackingNo.trim()) {
      setError("⚠️ Please enter a tracking number.");
      return;
    }

    setLoading(true);

    try {
      const normalizedTrackingNo = trackingNo.trim().toUpperCase();
      if (!TRACKING_PATTERN.test(normalizedTrackingNo)) {
        setError("❌ Invalid tracking number format.");
        setLoading(false);
        return;
      }
      // console.log('$$basicauth', process.env.CLAIMS_USERNAME, process.env.CLAIMS_PASSWORD);

      const response = await claimsApi.get(
        CLAIMS_API_URL,
        // `http://localhost:5000/hrisext/searchClaims?transaction_no=${trackingNo}`,
        {
          params: {
            transaction_no: normalizedTrackingNo,
          },
          auth: {
            username: process.env.REACT_APP_CLAIMS_USERNAME,
            password: process.env.REACT_APP_CLAIMS_PASSWORD
          },
        }
      );

      const data = response.data?.dataResult;

      if (!data?.result0 || (Array.isArray(data.result0) && data.result0.length === 0)) {
        setError("❌ Tracking number not found.");
        setLoading(false);
        return;
      }

      setData(data);
      settransactionDetails(data.result0[0]);
      settrasnactionTracking(data.result1);
      settransactionIsPending(data.result2?.[0]?._is_pending === 1 || false);
      settransactionPendingRemarks(data.result2?.[0]?._pending_remarks || "");
    } catch (err) {
      console.error("Claim lookup failed:", err);
      setError("❌ Tracking number not found or server error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="d-flex justify-content-center align-items-start min-vh-100 py-5"
      style={{
        backgroundImage:
          'url("https://isacenter.org/wp-content/uploads/2024/04/CAPITOL-HD-EDITED.jpg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        overflowY: "auto",
      }}
    >
      <div
        className="card shadow-lg p-4 w-100 mx-2"
        style={{
          maxWidth: data ? "90%" : "600px",
          backgroundColor: "rgba(255, 255, 255, 0.92)",
          borderRadius: "1rem",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div className="text-center mb-4">
          <img
            src={claimsLogo}
            alt="Claims Tracking Logo"
            className="img-fluid"
            style={{ maxWidth: "300px", height: "auto" }}
          />
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch}>
          <div className="mb-3">
            <label className="form-label fw-semibold d-block text-center">
              Tracking Number
            </label>
            <input
              type="text"
              className="form-control mx-auto text-center"
              style={{ maxWidth: "100%", width: "100%", minWidth: "200px" }}
              value={trackingNo}
              onChange={(e) => setTrackingNo(e.target.value)}
              placeholder="Enter tracking number (e.g., 24MM11-**CY****)"
            />
          </div>

          {/* Turnstile CAPTCHA */}
          <div className="d-flex justify-content-center mb-3">
            <Turnstile
              sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
              onVerify={(token) => setTurnstileToken(token)}
              theme="light"
            />
          </div>

          {/* Submit Button */}
          <div className="d-flex justify-content-center">
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              style={{ width: "100%", maxWidth: "400px" }}
            >
              {/* {loading ? "🔍 Searching..." : "Track Claim"} */}
              {loading ? (
                <>
                  <img src={speedrabbit} alt="Running Rabbit" style={{ width: "24px", marginRight: "8px" }} />
                  Searching...
                </>
              ) : (
                "🔍 Track Claim"
              )}
            </button>
          </div>

          <br />

          {/* Transaction Details */}
          {transactionDetails && (
            <div className="border rounded bg-light p-3 m-0">
              <h6 className="mb-3">Transaction Details</h6>
              {transactionIsPending && (
                <div className="alert alert-danger py-2 mb-3">
                  <strong>Pending:</strong> {transactionPendingRemarks}
                </div>
              )}

              <div className="row mb-1">
                <div className="col-5 fw-semibold text-muted">Name of Payee:</div>
                <div className="col-7"><strong>{transactionDetails.payee_name}</strong></div>
              </div>
              <div className="row mb-1">
                <div className="col-5 fw-semibold text-muted">Voucher Number:</div>
                <div className="col-7">{transactionDetails.voucher_number}</div>
              </div>
              <div className="row mb-1">
                <div className="col-5 fw-semibold text-muted">Particulars:</div>
                <div className="col-7">{transactionDetails.particulars}</div>
              </div>
              <div className="row mb-1">
                <div className="col-5 fw-semibold text-muted">Amount:</div>
                {/* <div className="col-7"><strong>{transactionDetails.amount}</strong></div> */}
                <div className="col-7">
                  <strong>
                    {Number(transactionDetails.amount).toLocaleString("en-PH", {
                      style: "decimal",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </strong>
                </div>
              </div>
            </div>
          )}

          {/* Transaction Tracking Table */}
          {trasnactionTracking && trasnactionTracking.length > 0 && (
            <div className="mt-4 table-responsive">
              <h6 className="mb-3">Transaction Tracking History</h6>
              <table className="table table-bordered align-middle text-center mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Office</th>
                    <th>Division</th>
                    <th>Status</th>
                    <th>Person In-Charge</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {trasnactionTracking.map((track, index) => (
                    <tr key={index}>
                      <td>{track._date}</td>
                      <td>{track._time}</td>
                      <td>{track.office_abb}</td>
                      <td>{track.division}</td>
                      <td>{track.status_name}</td>
                      <td>{track.person_incharge}</td>
                      <td>{track.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </form>

        {error && <div className="alert alert-danger mt-3">{error}</div>}

        <p className="text-center text-muted mt-4 small">
          {`© 2025 Provincial Government of Bohol | BD: 02162026-2`}
        </p>
      </div>
    </div>
  );
}

export default App;
